import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { authApi } from '../../api/auth';
import { errorMessage } from '../../api/client';
import { useAuthRouteState, type PendingConfirmation } from '../../auth/route-state';
import { Alert, Button, TextField } from '../../components/ui';

function deliveryLine(c: PendingConfirmation): string {
  if (c.medium === 'SMS') return `Enter the code we texted to ${c.destination ?? 'your phone'}.`;
  return `Enter the code we emailed to ${c.destination ?? 'your email address'}.`;
}

/**
 * Sign-up step 2 (POST /auth/confirm): the code the provider sent. Reached from
 * sign-up, or from sign-in when the account has not confirmed yet. On
 * success the user signs in with the password they chose.
 */
export function ConfirmSignUp() {
  const navigate = useNavigate();
  const { confirm: initial } = useAuthRouteState();
  const [pending, setPending] = useState<PendingConfirmation | undefined>(initial);
  const [code, setCode] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const confirm = useMutation({
    mutationFn: async () => {
      if (!pending) return;
      await authApi.confirmSignUp(pending.email, code.trim());
      navigate('/sign-in', {
        replace: true,
        state: {
          email: pending.email,
          notice: 'Your account is confirmed. Sign in to continue.',
        },
      });
    },
  });

  const resend = useMutation({
    mutationFn: async () => {
      if (!pending) return;
      setNotice(null);
      const delivery = await authApi.resendConfirmation(pending.email);
      setPending({ ...pending, ...delivery });
      setNotice(
        delivery.destination
          ? `A new code is on its way to ${delivery.destination}.`
          : 'If the account exists, a new code is on its way.',
      );
    },
  });

  if (!pending) return <Navigate to="/sign-up" replace />;

  return (
    <>
      <h1 className="auth__title">Confirm your account</h1>
      <p className="auth__lead">{deliveryLine(pending)}</p>
      {notice ? <Alert kind="info">{notice}</Alert> : null}
      {resend.error ? <Alert kind="info">{errorMessage(resend.error)}</Alert> : null}
      {confirm.error ? <Alert kind="error">{errorMessage(confirm.error)}</Alert> : null}
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          confirm.mutate();
        }}
        noValidate
      >
        <TextField
          label="Confirmation code"
          name="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          required
          maxLength={10}
          value={code}
          hint={
            <>
              Confirming <strong>{pending.email}</strong>.
            </>
          }
          onChange={(e) => setCode(e.target.value)}
        />
        <Button type="submit" loading={confirm.isPending} disabled={code.trim().length < 4}>
          Confirm
        </Button>
      </form>
      <p className="auth__switch">
        Didn&rsquo;t get it?{' '}
        <button
          type="button"
          className="linklike"
          onClick={() => resend.mutate()}
          disabled={resend.isPending}
        >
          Send a new code
        </button>{' '}
        · <Link to="/sign-in">Back to sign in</Link>
      </p>
    </>
  );
}
